<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              创建子账户
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              为团队成员创建独立账户，配置访问权限和数据脱敏设置
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="-mb-px flex space-x-8">
            <button
              @click="activeTab = 'basic'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              基本信息
            </button>
            <button
              @click="activeTab = 'permissions'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'permissions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              权限配置
            </button>
          </nav>
        </div>

        <!-- Tab 内容 -->
        <form @submit.prevent="handleSubmit">
          <!-- 基本信息 Tab -->
          <div v-show="activeTab === 'basic'" class="space-y-4 mb-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- 邮箱 -->
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                  邮箱地址 <span class="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  required
                  class="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  placeholder="user@example.com"
                />
                <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
              </div>

              <!-- 用户名 -->
              <div>
                <label for="username" class="block text-sm font-medium text-gray-700 mb-2">
                  用户名 <span class="text-red-500">*</span>
                </label>
                <input
                  id="username"
                  v-model="form.username"
                  type="text"
                  required
                  class="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  placeholder="username"
                />
                <p v-if="errors.username" class="mt-1 text-sm text-red-600">{{ errors.username }}</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- 密码 -->
              <div>
                <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
                  密码 <span class="text-red-500">*</span>
                </label>
                <input
                  id="password"
                  v-model="form.password"
                  type="password"
                  required
                  class="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  placeholder="至少8位字符"
                  minlength="8"
                />
                <p v-if="errors.password" class="mt-1 text-sm text-red-600">{{ errors.password }}</p>
              </div>

              <!-- 确认密码 -->
              <div>
                <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">
                  确认密码 <span class="text-red-500">*</span>
                </label>
                <input
                  id="confirmPassword"
                  v-model="form.confirmPassword"
                  type="password"
                  required
                  class="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  placeholder="重复输入密码"
                />
                <p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-600">{{ errors.confirmPassword }}</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- 部门 -->
              <div>
                <label for="department" class="block text-sm font-medium text-gray-700 mb-2">
                  部门
                </label>
                <input
                  id="department"
                  v-model="form.department"
                  type="text"
                  class="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  placeholder="如：销售部"
                />
              </div>

              <!-- 职位 -->
              <div>
                <label for="position" class="block text-sm font-medium text-gray-700 mb-2">
                  职位
                </label>
                <input
                  id="position"
                  v-model="form.position"
                  type="text"
                  class="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  placeholder="如：销售经理"
                />
              </div>
            </div>

            <!-- 手机号 -->
            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
                手机号
              </label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                class="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                placeholder="如：13800138000"
              />
            </div>
          </div>

          <!-- 权限配置 Tab -->
          <div v-show="activeTab === 'permissions'" class="space-y-6 mb-6">
            
            <!-- 邮件账户权限 -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="text-sm font-medium text-gray-700">
                  可操作的邮件账户
                </label>
                <!-- 🔍 搜索框 -->
                <input
                  v-model="emailAccountSearchQuery"
                  type="text"
                  placeholder="搜索邮件账户..."
                  class="w-48 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <!-- 🔧 固定高度 + 滚动 -->
              <div v-if="availableResources.email_accounts?.length > 0" class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredEmailAccounts.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="account in filteredEmailAccounts" :key="account.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="account.id"
                      v-model="form.accessibleEmailAccounts"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-3 py-2 text-sm font-medium transition-colors border"
                      :class="form.accessibleEmailAccounts.includes(account.id) 
                        ? 'bg-blue-100 text-blue-800 border-blue-300' 
                        : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'"
                    >
                      <div class="text-left">
                        <div class="font-medium">{{ account.display_name || account.email }}</div>
                        <div class="text-xs opacity-75">{{ account.email }}</div>
                      </div>
                    </span>
                  </label>
                </div>
                <!-- 空状态 -->
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  {{ emailAccountSearchQuery ? '未找到匹配的邮件账户' : '暂无可用邮件账户' }}
                </div>
              </div>
              <div v-else class="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                暂无可分配的邮件账户
              </div>
            </div>

            <!-- 客户分组权限 -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="text-sm font-medium text-gray-700">
                  可操作的客户分组
                </label>
                <!-- 🔍 搜索框 -->
                <input
                  v-model="customerGroupSearchQuery"
                  type="text"
                  placeholder="搜索客户分组..."
                  class="w-48 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <!-- 🔧 固定高度 + 滚动 -->
              <div v-if="availableResources.customer_groups?.length > 0" class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredCustomerGroups.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="group in filteredCustomerGroups" :key="group.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="group.id"
                      v-model="form.accessibleCustomerGroups"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-3 py-2 text-sm font-medium transition-colors border"
                      :class="form.accessibleCustomerGroups.includes(group.id) 
                        ? 'bg-orange-100 text-orange-800 border-orange-300' 
                        : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'"
                    >
                      <div class="text-left">
                        <div class="font-medium">{{ group.name }}</div>
                        <div class="text-xs opacity-75">{{ group.customer_count || 0 }} 个客户</div>
                      </div>
                    </span>
                  </label>
                </div>
                <!-- 空状态 -->
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  {{ customerGroupSearchQuery ? '未找到匹配的客户分组' : '暂无可用客户分组' }}
                </div>
              </div>
              <div v-else class="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                暂无可分配的客户分组
              </div>
            </div>

            <!-- 邮箱脱敏设置 -->
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label class="flex items-start cursor-pointer">
                <input
                  v-model="form.maskCustomerEmails"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                />
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900">脱敏客户邮箱地址</div>
                  <div class="text-sm text-gray-600 mt-1">开启后，子账户将无法看到完整的客户邮箱地址</div>
                </div>
              </label>
            </div>
          </div>


          <!-- 操作按钮 -->
          <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              @click="closeModal"
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
              :disabled="submitting"
            >
              取消
            </button>
            
            <button 
              @click="handleSubmit"
              type="button"
              :disabled="submitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
            >
              <svg v-if="submitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ submitting ? '创建中...' : '创建子账户' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { subAccountService } from '@/services/subAccountService'

const toast = useToast()

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  availableResources: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'created'])

// Tab 状态
const activeTab = ref('basic')

// 🔍 搜索查询
const emailAccountSearchQuery = ref('')
const customerGroupSearchQuery = ref('')

// 表单数据
const form = reactive({
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  department: '',
  position: '',
  phone: '',
  accessibleEmailAccounts: [],
  accessibleCustomerGroups: [],
  maskCustomerEmails: true
})

// 状态
const submitting = ref(false)
const errors = ref({})

// 🔧 过滤邮件账户
const filteredEmailAccounts = computed(() => {
  const accounts = props.availableResources.email_accounts || []
  if (!emailAccountSearchQuery.value.trim()) {
    return accounts
  }
  
  const query = emailAccountSearchQuery.value.toLowerCase()
  return accounts.filter(account => {
    const displayName = (account.display_name || '').toLowerCase()
    const email = (account.email || '').toLowerCase()
    return displayName.includes(query) || email.includes(query)
  })
})

// 🔧 过滤客户分组
const filteredCustomerGroups = computed(() => {
  const groups = props.availableResources.customer_groups || []
  if (!customerGroupSearchQuery.value.trim()) {
    return groups
  }
  
  const query = customerGroupSearchQuery.value.toLowerCase()
  return groups.filter(group => {
    const name = (group.name || '').toLowerCase()
    return name.includes(query)
  })
})

// Watch for modal show/hide
watch(() => props.show, (show) => {
  if (show) {
    resetState()
  }
})

// 重置状态
const resetState = () => {
  activeTab.value = 'basic'
  emailAccountSearchQuery.value = ''
  customerGroupSearchQuery.value = ''
  Object.assign(form, {
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    department: '',
    position: '',
    phone: '',
    accessibleEmailAccounts: [],
    accessibleCustomerGroups: [],
    maskCustomerEmails: true
  })
  errors.value = {}
  submitting.value = false
}

// 关闭模态框
const closeModal = () => {
  if (!submitting.value) {
    emit('close')
  }
}

// 验证表单
const validateForm = () => {
  errors.value = {}
  
  // 邮箱验证
  if (!form.email) {
    errors.value.email = '邮箱地址不能为空'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.value.email = '请输入有效的邮箱地址'
  }
  
  // 用户名验证
  if (!form.username) {
    errors.value.username = '用户名不能为空'
  } else if (form.username.length < 3) {
    errors.value.username = '用户名至少3个字符'
  }
  
  // 密码验证
  if (!form.password) {
    errors.value.password = '密码不能为空'
  } else if (form.password.length < 8) {
    errors.value.password = '密码至少8个字符'
  }
  
  // 确认密码验证
  if (!form.confirmPassword) {
    errors.value.confirmPassword = '请确认密码'
  } else if (form.password !== form.confirmPassword) {
    errors.value.confirmPassword = '密码确认不匹配'
  }
  
  return Object.keys(errors.value).length === 0
}

// 提交表单
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }
  
  try {
    submitting.value = true
    
    const data = {
      email: form.email,
      username: form.username,
      password: form.password,
      confirm_password: form.confirmPassword,
      department: form.department,
      position: form.position,
      phone: form.phone,
      accessible_email_accounts: form.accessibleEmailAccounts,
      accessible_customer_groups: form.accessibleCustomerGroups,
      mask_customer_emails: form.maskCustomerEmails
    }
    
    await subAccountService.createSubAccount(data)
    toast.success('子账户创建成功')
    emit('created')
    
  } catch (error) {
    console.error('创建子账户失败:', error)
    
    // 处理400错误（验证错误）
    if (error.response?.status === 400) {
      const responseData = error.response.data
      let errorData = null
      
      // 支持多种错误数据格式
      if (responseData.error_data) {
        // 格式1: {error_data: {email: [...]}}
        errorData = responseData.error_data
      } else if (responseData.email || responseData.username || responseData.password) {
        // 格式2: 直接的字段错误 {email: [...], username: [...]}
        errorData = responseData
      }
      
      if (errorData) {
        // 处理字段级别的验证错误
        const fieldErrors = {}
        const errorMessages = []
        
        for (const [field, messages] of Object.entries(errorData)) {
          if (Array.isArray(messages) && messages.length > 0) {
            const message = messages[0].string || messages[0]
            // 在输入框下方显示错误
            fieldErrors[field] = message
            // 同时收集错误消息用于toast
            const fieldName = field === 'email' ? '邮箱' : 
                             field === 'username' ? '用户名' : 
                             field === 'password' ? '密码' : field
            errorMessages.push(`${fieldName}: ${message}`)
          }
        }
        
        errors.value = fieldErrors
        // 显示toast错误提示
        if (errorMessages.length > 0) {
          toast.error(errorMessages.join('；'))
        }
        return // 处理完毕，直接返回
      }
    } else if (error.response?.data?.errors) {
      // 兼容其他错误格式
      errors.value = error.response.data.errors
      toast.error('请检查输入信息')
    } else if (error.response?.data?.error) {
      toast.error(error.response.data.error)
    } else if (error.response?.data?.exception_message) {
      toast.error(error.response.data.exception_message)
    } else {
      toast.error(error.message || '创建失败，请重试')
    }
  } finally {
    submitting.value = false
  }
}
</script>
